import {
  FC,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Box, Button, Divider, Stack, Typography, FormControl, Select, MenuItem } from "@mui/material";

import { filter_black_icon } from "@/constants";
import { DateRangeFilter, DialogMenu } from "@/components";
import { tokens } from "@/tokens";

export const initialFilters = {
  adjustmentType: "" as "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty",
  startDate: new Date("1 Jan 2000"),
  endDate: new Date(),
};

type Filters = {
  adjustmentType: "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty";
  startDate: Date;
  endDate: Date;
};

const Fiters: FC<{ onFiltersChange: (filters: Filters) => void }> = ({
  onFiltersChange,
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [filters, setFilters] = useState<Filters>(initialFilters);

  const handleAdjustmentTypeChange = useCallback(
    (adjustmentType: "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty") => {
      setFilters({
        ...filters,
        adjustmentType,
      });
    },
    [filters]
  );

  const handleRangeDate = useCallback(
    (startDate?: Date, endDate?: Date) => {
      if (startDate && endDate) {
        setFilters({
          ...filters,
          startDate,
          endDate,
        });
      }
    },
    [filters]
  );

  const openFilterModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const closeFilterModal = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const applyFilters = (): void => {
    onFiltersChange(filters);
    closeFilterModal();
  };

  const resetFilters = useCallback((): void => {
    setFilters(initialFilters);
  }, []);

  const appliedFilterLength = useMemo(() => {
    let appliedFiltersCount = 0;

    if (filters.adjustmentType !== initialFilters.adjustmentType) {
      appliedFiltersCount++;
    }

    if (
      filters.startDate !== initialFilters.startDate ||
      filters.endDate !== initialFilters.endDate
    ) {
      appliedFiltersCount++;
    }

    return appliedFiltersCount;
  }, [filters]);

  return (
    <>
      <Button
        variant="white-contained"
        onClick={openFilterModal}
        id="advance-filter-anchor"
        aria-controls={anchorEl ? "post-import-filters-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        sx={{
          flex: {
            xs: 1,
            sm: "initial",
          },
          fontWeight: tokens.fontWeight.semibold,
          fontSize: tokens.fontSize.md,
          borderRadius: tokens.radius.md,
          height: 36,
          px: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <img src={filter_black_icon} alt="filter-icon" />
          <Typography variant="body1" color={tokens.brand.primary} fontWeight={tokens.fontWeight.semibold}>
            Filter
          </Typography>
          {appliedFilterLength > 0 && (
            <Box
              sx={{
                width: 24,
                height: 24,
                background: "#0B494B26",
                display: "grid",
                placeContent: "center",
                borderRadius: "50%",
              }}
            >
              <Typography variant="caption" color={tokens.brand.primary} fontWeight={tokens.fontWeight.semibold}>
                {appliedFilterLength}
              </Typography>
            </Box>
          )}
        </Stack>
      </Button>
      <DialogMenu
        id="advance-filter-menu"
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeFilterModal}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={2.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src={filter_black_icon} alt="filter" />
            <Typography
              variant="h5"
              fontSize={18}
              fontWeight={tokens.fontWeight.bold}
              color={tokens.text.primary}
            >
              Filter
            </Typography>
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "grid",
                placeContent: "center",
                backgroundColor: tokens.status.danger,
                color: "white",
                borderRadius: "50%",
              }}
            >
              <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} color="white">
                {appliedFilterLength}
              </Typography>
            </Box>
          </Stack>

          <Typography
            sx={{
              color: tokens.brand.primary,
              textDecorationLine: "underline",
              cursor: "pointer",
            }}
            variant="body2"
            fontWeight={tokens.fontWeight.semibold}
            onClick={resetFilters}
          >
            Clear All
          </Typography>
        </Stack>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <Box>
            <Typography
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: tokens.text.primary,
              }}
              variant="subtitle2"
            >
              Adjustment Type
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={filters.adjustmentType}
                onChange={(e) => handleAdjustmentTypeChange(e.target.value as any)}
                displayEmpty
                sx={{
                  backgroundColor: tokens.bg.surface,
                  "& .MuiSelect-select": {
                    py: 1,
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Adjustment Types</em>
                </MenuItem>
                <MenuItem value="value_adjustment">Value Adjustment</MenuItem>
                <MenuItem value="duty_rate_adjustment">Duty Rate Adjustment</MenuItem>
                <MenuItem value="combined_value_and_duty">Combined Value and Duty Adjustment</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: tokens.text.primary,
              }}
              variant="subtitle2"
            >
              Date & Time
            </Typography>
            <DateRangeFilter
              value={{
                startDate: filters.startDate,
                endDate: filters.endDate,
              }}
              onSelectDate={({ startDate, endDate }) =>
                handleRangeDate(startDate, endDate)
              }
            />
          </Box>
        </Stack>
        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={1}
          p={2}
          sx={{
            background: "#F7F8F8",
          }}
        >
          <Button
            variant="white-outlined"
            sx={{ flex: 1, fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, color: tokens.text.body, bgcolor: tokens.bg.surface, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
            onClick={closeFilterModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ flex: 1, gap: 1, fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, bgcolor: tokens.brand.primary, "&:hover": { bgcolor: tokens.brand.primaryHover }, boxShadow: "none" }}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </Stack>
      </DialogMenu>
    </>
  );
};

export default Fiters;
